import styled from '@emotion/styled'
import { OrderItem } from 'shop/types/cart'
import Quantity from '../Quantity/Quantity'
import Price from '../Price/Price'
import { ModifierList } from '../Modifiers'
import { OptionList } from '../Options'
import { Name } from '../commonStyles'
import { CartType } from '../Cart'
import QuantityControl from 'shop/components/QuantityControl/QuantityControl'
import { StyledHTMLElement } from 'shop/theme/types'
import { StyledBinIcon } from 'shop/components/Shop/commonIcons'

interface Props {
  orderItem: OrderItem
  type: CartType
  onQuantityChange?: (orderItem: OrderItem, delta: number) => Promise<void>
  isLast?: boolean
}

const CartItem = ({
  orderItem,
  type,
  onQuantityChange,
  isLast = false
}: Props) => {
  const {
    name,
    quantity,
    total: itemTotal,
    modifierGroups,
    options
  } = orderItem
  const isCartModal = type === CartType.Cart

  /** Call quantity change callback with orderItem and new quantity value */
  const handleQuantityChange = (delta: number) => {
    if (onQuantityChange) {
      onQuantityChange(orderItem, delta)
    }
  }

  const detailedContent = (
    <DetailedContent data-testid='cart-item-detailed' type={type}>
      {!!options && <OptionList options={options} />}
      {!!modifierGroups.length && (
        <ModifierList modifierGroups={modifierGroups} />
      )}
    </DetailedContent>
  )

  return (
    <ContentWrapper type={type} isLast={isLast}>
      <BasicContent data-testid='cart-item-basic' type={type}>
        {!isCartModal && <Quantity quantity={quantity} />}
        <ProductName isCartModal={isCartModal}>{name}</ProductName>
        {!isCartModal && <Price price={itemTotal}></Price>}
      </BasicContent>
      {detailedContent}
      {isCartModal && (
        <ControlContent>
          <Price
            type={isCartModal ? 'horizontal' : 'vertical'}
            price={itemTotal}
          ></Price>
          <QuantityControl
            quantity={quantity}
            width={'32px'}
            onIncrement={() => handleQuantityChange(1)}
            onDecrement={() => handleQuantityChange(-1)}
            customDecrementIcon={quantity === 1 ? <StyledBinIcon /> : undefined}
          />
        </ControlContent>
      )}
    </ContentWrapper>
  )
}

const ContentWrapper = styled.li<
  StyledHTMLElement & { type: CartType; isLast: boolean }
>(({ type, isLast }) => {
  const isCartModal = type === CartType.Cart
  return {
    display: isCartModal ? 'block' : 'grid',
    gridTemplateColumns: isCartModal ? undefined : '38px 1fr 75px',
    marginBottom: isLast && isCartModal ? '0px' : '20px',
    listStyle: 'none'
  }
})

const BasicContent = styled.div<StyledHTMLElement & { type: CartType }>(
  ({ type }) => {
    const isCartModal = type === CartType.Cart
    return {
      marginBottom: isCartModal ? '6px' : '0',
      display: isCartModal ? 'block' : 'contents',
      fontWeight: 500
    }
  }
)

const DetailedContent = styled.div<StyledHTMLElement & { type: CartType }>(
  ({ type }) => {
    const isCartModal = type === CartType.Cart
    return {
      marginBottom: isCartModal ? '8px' : '0',
      'p, span': {
        fontSize: '14px'
      },
      maxWidth: '82%',
      gridColumn: '2 / -1'
    }
  }
)

const ControlContent = styled.div(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}))

const ProductName = styled(Name)<{ isCartModal: boolean }>(
  ({ theme, isCartModal }) => ({
    fontSize: '16px',
    margin: '0',
    textOverflow: 'ellipsis',
    whiteSpace: isCartModal ? 'normal' : 'nowrap',
    overflow: 'hidden',
    fontWeight: 600,
    [theme.mediaQueries.viewport6]: {
      whiteSpace: 'nowrap'
    }
  })
)

export default CartItem
